import { nanoid } from 'nanoid';

/**
 * JSON Canvas 1.0 model (jsoncanvas.org) with lossless round-trips.
 *
 * Every node/edge keeps its original parsed JSON object in `raw`; serialize
 * spreads `raw` back and only overrides the fields this app manages, so
 * unknown fields written by Obsidian (or future spec versions) survive
 * view + edit round-trips untouched.
 */

export type Side = 'top' | 'right' | 'bottom' | 'left';

export interface CanvasNode {
  id: string;
  /** 'text' | 'file' | 'link' | 'group' — unknown types are kept and rendered
   * as a generic card so they are never dropped. */
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** '1'..'6' preset or '#rrggbb'. */
  color?: string;
  text?: string;
  file?: string;
  url?: string;
  label?: string;
  /** Original parsed object; unknown fields preserved through serialize. */
  raw: Record<string, unknown>;
}

export interface CanvasEdge {
  id: string;
  fromNode: string;
  fromSide?: Side;
  toNode: string;
  toSide?: Side;
  color?: string;
  label?: string;
  raw: Record<string, unknown>;
}

export interface CanvasModel {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  /** Original top-level object (minus live nodes/edges) for unknown fields. */
  raw: Record<string, unknown>;
}

export const newCanvasId = (): string => nanoid(16);

// -- parsing -----------------------------------------------------------------

const num = (v: unknown, d = 0): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : d;
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const SIDE_SET = new Set<string>(['top', 'right', 'bottom', 'left']);
const sideOf = (v: unknown): Side | undefined =>
  typeof v === 'string' && SIDE_SET.has(v) ? (v as Side) : undefined;

function parseNode(v: unknown): CanvasNode | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const raw = v as Record<string, unknown>;
  return {
    id: str(raw.id) ?? newCanvasId(),
    type: str(raw.type) ?? 'text',
    x: num(raw.x),
    y: num(raw.y),
    width: num(raw.width, 250),
    height: num(raw.height, 60),
    color: str(raw.color),
    text: str(raw.text),
    file: str(raw.file),
    url: str(raw.url),
    label: str(raw.label),
    raw,
  };
}

function parseEdge(v: unknown): CanvasEdge | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const raw = v as Record<string, unknown>;
  return {
    id: str(raw.id) ?? newCanvasId(),
    fromNode: str(raw.fromNode) ?? '',
    fromSide: sideOf(raw.fromSide),
    toNode: str(raw.toNode) ?? '',
    toSide: sideOf(raw.toSide),
    color: str(raw.color),
    label: str(raw.label),
    raw,
  };
}

/** Tolerant parse: empty/invalid JSON yields an empty canvas. */
export function parseCanvas(text: string): CanvasModel {
  let json: unknown = {};
  try {
    if (text.trim()) json = JSON.parse(text);
  } catch {
    json = {};
  }
  const root =
    json && typeof json === 'object' && !Array.isArray(json)
      ? (json as Record<string, unknown>)
      : {};
  const nodes: CanvasNode[] = [];
  if (Array.isArray(root.nodes)) {
    for (const n of root.nodes) {
      const parsed = parseNode(n);
      if (parsed) nodes.push(parsed);
    }
  }
  const edges: CanvasEdge[] = [];
  if (Array.isArray(root.edges)) {
    for (const e of root.edges) {
      const parsed = parseEdge(e);
      if (parsed) edges.push(parsed);
    }
  }
  return { nodes, edges, raw: root };
}

/** Spread raw, override managed keys. A managed key whose model value is
 * undefined is removed only when the raw value was a plain string (i.e. it was
 * something we lifted into the model and the user cleared it) — wrong-typed
 * unknown data is never dropped. */
function mergeManaged(
  raw: Record<string, unknown>,
  managed: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...raw };
  for (const [k, v] of Object.entries(managed)) {
    if (v !== undefined) out[k] = v;
    else if (typeof out[k] === 'string') delete out[k];
  }
  return out;
}

/** Serialize with tab indentation (matches Obsidian's .canvas formatting). */
export function serializeCanvas(model: CanvasModel): string {
  const out: Record<string, unknown> = {
    ...model.raw,
    nodes: model.nodes.map((n) =>
      mergeManaged(n.raw, {
        id: n.id,
        type: n.type,
        x: n.x,
        y: n.y,
        width: n.width,
        height: n.height,
        color: n.color,
        text: n.text,
        file: n.file,
        url: n.url,
        label: n.label,
      }),
    ),
    edges: model.edges.map((e) =>
      mergeManaged(e.raw, {
        id: e.id,
        fromNode: e.fromNode,
        fromSide: e.fromSide,
        toNode: e.toNode,
        toSide: e.toSide,
        color: e.color,
        label: e.label,
      }),
    ),
  };
  return JSON.stringify(out, null, '\t');
}

// -- colors ------------------------------------------------------------------

/** JSON Canvas preset palette (Obsidian's defaults). */
export const PRESET_COLORS: Record<string, string> = {
  '1': '#e93147', // red
  '2': '#ec7500', // orange
  '3': '#e0ac00', // yellow
  '4': '#08b94e', // green
  '5': '#00bfbc', // cyan
  '6': '#7852ee', // purple
};

export function resolveColor(c: string | undefined): string | null {
  if (!c) return null;
  return PRESET_COLORS[c] ?? (c.startsWith('#') ? c : null);
}

// -- geometry ----------------------------------------------------------------

export interface Pt {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Viewport {
  x: number;
  y: number;
  scale: number;
}

export interface TempEdge {
  fromNode: string;
  fromSide: Side;
  to: Pt;
}

export const GRID = 10;
export const MIN_W = 60;
export const MIN_H = 40;
export const MIN_SCALE = 0.05;
export const MAX_SCALE = 4;

export const snap = (v: number, grid = GRID): number => Math.round(v / grid) * grid;
export const clampScale = (s: number): number =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

export const SIDES: readonly Side[] = ['top', 'right', 'bottom', 'left'];

export function sideAnchor(r: Rect, s: Side): Pt {
  switch (s) {
    case 'top':
      return { x: r.x + r.width / 2, y: r.y };
    case 'bottom':
      return { x: r.x + r.width / 2, y: r.y + r.height };
    case 'left':
      return { x: r.x, y: r.y + r.height / 2 };
    case 'right':
      return { x: r.x + r.width, y: r.y + r.height / 2 };
  }
}

export function sideDir(s: Side): Pt {
  switch (s) {
    case 'top':
      return { x: 0, y: -1 };
    case 'bottom':
      return { x: 0, y: 1 };
    case 'left':
      return { x: -1, y: 0 };
    case 'right':
      return { x: 1, y: 0 };
  }
}

export const pointRect = (p: Pt): Rect => ({ x: p.x, y: p.y, width: 0, height: 0 });

/** Pick the best (from, to) side pair: shortest anchor distance, strongly
 * preferring sides that face each other. Fixed sides constrain the search. */
export function autoSides(
  a: Rect,
  b: Rect,
  fixedFrom?: Side,
  fixedTo?: Side,
): [Side, Side] {
  const fromCands = fixedFrom ? [fixedFrom] : SIDES;
  const toCands = fixedTo ? [fixedTo] : SIDES;
  let best: [Side, Side] = [fromCands[0], toCands[0]];
  let bestScore = Infinity;
  for (const sa of fromCands) {
    for (const sb of toCands) {
      const pa = sideAnchor(a, sa);
      const pb = sideAnchor(b, sb);
      const dx = pb.x - pa.x;
      const dy = pb.y - pa.y;
      const d = Math.hypot(dx, dy) || 1;
      const da = sideDir(sa);
      const db = sideDir(sb);
      // facing ∈ [-2, 2]: 2 = both sides point straight at each other.
      const facing = (da.x * dx + da.y * dy) / d - (db.x * dx + db.y * dy) / d;
      const score = d * (2.2 - facing);
      if (score < bestScore) {
        bestScore = score;
        best = [sa, sb];
      }
    }
  }
  return best;
}

export interface EdgeGeometry {
  /** SVG path data (cubic bezier). */
  d: string;
  from: Pt;
  to: Pt;
  fromSide: Side;
  toSide: Side;
  /** Bezier point at t=0.5 (label position). */
  mid: Pt;
  /** Outward direction at the target anchor (for the arrowhead). */
  toDir: Pt;
}

export function edgeGeometry(
  a: Rect,
  b: Rect,
  fromSide?: Side,
  toSide?: Side,
): EdgeGeometry {
  const [sa, sb] = autoSides(a, b, fromSide, toSide);
  const pa = sideAnchor(a, sa);
  const pb = sideAnchor(b, sb);
  const da = sideDir(sa);
  const db = sideDir(sb);
  const dist = Math.hypot(pb.x - pa.x, pb.y - pa.y);
  const k = Math.min(260, Math.max(40, dist * 0.5));
  const c1 = { x: pa.x + da.x * k, y: pa.y + da.y * k };
  const c2 = { x: pb.x + db.x * k, y: pb.y + db.y * k };
  const d = `M ${pa.x} ${pa.y} C ${c1.x} ${c1.y} ${c2.x} ${c2.y} ${pb.x} ${pb.y}`;
  const mid = {
    x: (pa.x + 3 * c1.x + 3 * c2.x + pb.x) / 8,
    y: (pa.y + 3 * c1.y + 3 * c2.y + pb.y) / 8,
  };
  return { d, from: pa, to: pb, fromSide: sa, toSide: sb, mid, toDir: db };
}

export function nearestSide(r: Rect, p: Pt): Side {
  let best: Side = 'left';
  let bd = Infinity;
  for (const s of SIDES) {
    const a = sideAnchor(r, s);
    const d = (a.x - p.x) ** 2 + (a.y - p.y) ** 2;
    if (d < bd) {
      bd = d;
      best = s;
    }
  }
  return best;
}

export function rectContains(r: Rect, p: Pt): boolean {
  return p.x >= r.x && p.y >= r.y && p.x <= r.x + r.width && p.y <= r.y + r.height;
}

export function containsRect(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

/** Topmost node under a point: non-groups first (last in model order = on
 * top), then the smallest containing group. */
export function nodeAtPoint(
  nodes: readonly CanvasNode[],
  p: Pt,
  excludeId?: string,
): CanvasNode | null {
  let group: CanvasNode | null = null;
  let hit: CanvasNode | null = null;
  for (const n of nodes) {
    if (n.id === excludeId || !rectContains(n, p)) continue;
    if (n.type === 'group') {
      if (!group || n.width * n.height < group.width * group.height) group = n;
    } else {
      hit = n;
    }
  }
  return hit ?? group;
}

export function nodesBBox(nodes: readonly Rect[]): Rect | null {
  if (nodes.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    minX = Math.min(minX, n.x);
    minY = Math.min(minY, n.y);
    maxX = Math.max(maxX, n.x + n.width);
    maxY = Math.max(maxY, n.y + n.height);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
